import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiInformationCircle,
  HiDocumentText,
  HiGlobeAlt,
  HiClock,
  HiArrowDownTray,
  HiEye,
  HiLink,
  HiArrowsPointingOut,
} from 'react-icons/hi2';
import { HiX } from 'react-icons/hi';
import {
  documentVisibilityTypePillClassName,
  formatDocumentVisibilityTypeLabel,
} from './documentVisibility';

export default function DetailsModal({
  open,
  doc,
  onClose,
  isPreviewable,
  openPreview,
  formatFileSize,
  formatDate,
}) {
  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [open, handleKeyDown]);

  if (!open || !doc) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm sm:p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={e => e.stopPropagation()}
          className="relative flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
        >
          {/* HEADER (Fixed) */}
          <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-6">
            <div className="flex items-center gap-4">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', delay: 0.1 }}
                className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-600 text-white shadow-xl shadow-blue-500/40"
              >
                <HiInformationCircle className="h-7 w-7" />
              </motion.div>
              <div className="min-w-0 flex-1">
                <h3 className="text-2xl font-bold text-black">Document Details</h3>
                <p className="mt-1.5 truncate text-sm font-semibold text-gray-600">
                  {doc?.fileName || 'Unknown Document'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open(doc.url, '_blank')}
                className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 shadow-sm transition-all hover:bg-gray-50"
                title="Open in new tab"
              >
                <HiArrowsPointingOut className="h-5 w-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-700"
                title="Close (Esc)"
              >
                <HiX className="h-6 w-6" />
              </motion.button>
            </div>
          </div>

          {/* MAIN SCROLLABLE CONTENT */}
          <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex-1 overflow-y-auto bg-gray-50 p-6 sm:p-8">
            <div className="grid grid-cols-1 gap-6 pb-8 lg:grid-cols-2">
              {/* LEFT COLUMN */}
              <div className="space-y-6">
                {/* Basic Info */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-black/5"
                >
                  <h4 className="mb-5 flex items-center gap-3 text-lg font-bold text-black">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                      <HiDocumentText className="h-5 w-5 text-blue-600" />
                    </div>
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Info label="File Name" value={doc?.fileName || 'N/A'} />
                    <Info label="Category" value={doc?.category || 'N/A'} />
                    <Info label="File Type" value={doc?.fileType || 'N/A'} />
                    <Info label="File Size" value={formatFileSize(doc?.fileSize)} />
                    <Info label="Uploaded At" value={formatDate(doc?.uploadedAt)} />
                    <Info label="Uploaded By Role" value={doc?.uploadedByRole || 'N/A'} />
                  </div>
                </motion.div>

                {/* Notes */}
                {doc?.notes && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 }}
                    className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-black/5"
                  >
                    <h4 className="mb-4 flex items-center gap-3 text-lg font-bold text-black">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                        <HiDocumentText className="h-5 w-5 text-blue-600" />
                      </div>
                      Notes
                    </h4>
                    <p className="whitespace-pre-wrap text-sm font-medium leading-relaxed text-gray-700">
                      {doc.notes}
                    </p>
                  </motion.div>
                )}
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-6">
                {/* Visibility */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-black/5"
                >
                  <h4 className="mb-5 flex items-center gap-3 text-lg font-bold text-black">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                      <HiGlobeAlt className="h-5 w-5 text-green-600" />
                    </div>
                    Visibility Settings
                  </h4>
                  <div className="space-y-3">
                    <Info
                      label="Visibility Type"
                      value={
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${documentVisibilityTypePillClassName(doc?.visibility?.type)}`}
                        >
                          {formatDocumentVisibilityTypeLabel(doc?.visibility?.type)}
                        </span>
                      }
                    />
                    {Array.isArray(doc?.visibility?.roles) && doc.visibility.roles.length > 0 && (
                      <div>
                        <label className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          Allowed Roles ({doc.visibility.roles.length})
                        </label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {doc.visibility.roles.map((role, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-medium text-purple-800"
                            >
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {Array.isArray(doc?.visibility?.userIds) &&
                      doc.visibility.userIds.length > 0 && (
                        <Info
                          label={`Allowed Users (${doc.visibility.userIds.length})`}
                          value={`${doc.visibility.userIds.length} user(s) have access`}
                        />
                      )}
                  </div>
                </motion.div>

                {/* URL */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 }}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-black/5"
                >
                  <h4 className="mb-4 flex items-center gap-3 text-lg font-bold text-black">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                      <HiLink className="h-5 w-5 text-blue-600" />
                    </div>
                    Document URL
                  </h4>
                  <div className="flex items-center gap-3">
                    <input
                      readOnly
                      value={doc?.url || ''}
                      onClick={e => e.target.select()}
                      className="flex-1 rounded-lg border-2 border-gray-300 bg-gray-50 px-4 py-2.5 font-mono text-xs font-medium text-black shadow-sm transition-all hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => doc?.url && navigator.clipboard.writeText(doc.url)}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-xs font-bold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700"
                    >
                      <HiLink className="h-4 w-4" />
                      Copy
                    </motion.button>
                  </div>
                </motion.div>

                {/* Additional Metadata */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm ring-1 ring-black/5"
                >
                  <h4 className="mb-5 flex items-center gap-3 text-lg font-bold text-black">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                      <HiClock className="h-5 w-5 text-orange-600" />
                    </div>
                    Additional Metadata
                  </h4>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {doc?.ownerId && <Info label="Owner ID" value={doc.ownerId} mono />}
                    {doc?.ownerEmail && <Info label="Owner Email" value={doc.ownerEmail} />}
                    {doc?.scope && <Info label="Scope" value={doc.scope} />}
                    {doc?.storagePath && (
                      <Info label="Storage Path" value={doc.storagePath} mono break />
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          </div>

          {/* FOOTER (Fixed) */}
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-5">
            <div className="flex items-center gap-2.5 text-sm font-medium text-gray-600">
              <HiClock className="h-5 w-5 text-gray-500" />
              <span>Last updated: {formatDate(doc?.uploadedAt)}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {isPreviewable(doc?.fileType) && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    onClose();
                    openPreview(doc);
                  }}
                  className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-green-500/30 transition-all hover:bg-green-700"
                >
                  <HiEye className="h-4 w-4" />
                  Preview
                </motion.button>
              )}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={doc?.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700"
              >
                <HiArrowDownTray className="h-4 w-4" />
                Download
              </motion.a>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
              >
                Close
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* Small helper component for consistent layout */
function Info({ label, value, mono = false, break: br }) {
  return (
    <div>
      <label className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</label>
      <p
        className={`mt-1 text-sm font-semibold text-gray-900 ${
          mono ? 'font-mono text-xs' : ''
        } ${br ? 'break-all' : ''}`}
      >
        {value || 'N/A'}
      </p>
    </div>
  );
}
